import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LATERAL_STATUS_OPTIONS } from "@/lib/lateral-statuses";
import type { LateralJobPosition } from "@/types/database";

export interface LateralFilterValues {
  statusFilter: string;
  positionFilter: string;
  minScore: string;
  maxScore: string;
  minExp: string;
  maxExp: string;
  locationFilter: string;
  noticePeriodFilter: string;
}

export const emptyLateralFilters = (): LateralFilterValues => ({
  statusFilter: "all",
  positionFilter: "all",
  minScore: "",
  maxScore: "",
  minExp: "",
  maxExp: "",
  locationFilter: "",
  noticePeriodFilter: "",
});

interface Props {
  values: LateralFilterValues;
  onChange: (key: keyof LateralFilterValues, value: string) => void;
  positions: Pick<LateralJobPosition, "id" | "job_title">[];
  /** Hide status filter when the UI already segregates by status (e.g. Kanban columns) */
  showStatus?: boolean;
  /** Hide position filter when already filtered by position (e.g. Kanban) */
  showPosition?: boolean;
  /** Show an Apply button — used for server-side filtered views like Kanban */
  onApply?: () => void;
  onClear: () => void;
}

export function LateralFilterBar({
  values,
  onChange,
  positions,
  showStatus = true,
  showPosition = true,
  onApply,
  onClear,
}: Props) {
  const field =
    (key: keyof LateralFilterValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(key, e.target.value);

  return (
    <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Status — hidden on kanban since columns ARE statuses */}
        {showStatus && (
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={values.statusFilter} onValueChange={(v) => onChange("statusFilter", v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {LATERAL_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Job Position — hidden on per-position kanban */}
        {showPosition && (
          <div className="space-y-1.5">
            <Label className="text-xs">Job Position</Label>
            <Select value={values.positionFilter} onValueChange={(v) => onChange("positionFilter", v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Positions</SelectItem>
                {positions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.job_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* CV Score range */}
        <div className="space-y-1.5">
          <Label className="text-xs">CV Score Min</Label>
          <Input
            type="number"
            min={0}
            max={100}
            className="h-8 text-xs"
            placeholder="0"
            value={values.minScore}
            onChange={field("minScore")}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">CV Score Max</Label>
          <Input
            type="number"
            min={0}
            max={100}
            className="h-8 text-xs"
            placeholder="100"
            value={values.maxScore}
            onChange={field("maxScore")}
          />
        </div>

        {/* Experience range */}
        <div className="space-y-1.5">
          <Label className="text-xs">Experience Min (yrs)</Label>
          <Input
            type="number"
            min={0}
            step={0.5}
            className="h-8 text-xs"
            placeholder="0"
            value={values.minExp}
            onChange={field("minExp")}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Experience Max (yrs)</Label>
          <Input
            type="number"
            min={0}
            step={0.5}
            className="h-8 text-xs"
            placeholder="—"
            value={values.maxExp}
            onChange={field("maxExp")}
          />
        </div>

        {/* Location */}
        <div className="space-y-1.5">
          <Label className="text-xs">Location</Label>
          <Input
            className="h-8 text-xs"
            placeholder="e.g. Bangalore"
            value={values.locationFilter}
            onChange={field("locationFilter")}
          />
        </div>

        {/* Notice period */}
        <div className="space-y-1.5">
          <Label className="text-xs">Notice Period (contains)</Label>
          <Input
            className="h-8 text-xs"
            placeholder="e.g. 30 days"
            value={values.noticePeriodFilter}
            onChange={field("noticePeriodFilter")}
          />
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center gap-2 justify-end pt-1">
        <Button size="sm" variant="ghost" onClick={onClear} className="text-gray-500 h-7">
          Clear all
        </Button>
        {onApply && (
          <Button size="sm" onClick={onApply} className="h-7">
            Apply Filters
          </Button>
        )}
      </div>
    </div>
  );
}
